"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";

/**
 * Renders a visually-hidden sentinel element at the top of the DOM (before
 * the skip-nav link) and focuses it on every client-side route change.
 *
 * Because the sentinel sits before the skip-nav link in DOM order, the
 * browser places its tab cursor there, so the next Tab press lands on the
 * skip-nav link — exactly what happens after a full page load.
 *
 * The sentinel has tabIndex={-1} so it never appears in the natural tab
 * order, and aria-hidden so screen readers ignore the element itself.
 */
export function RouteChangeAnnouncer() {
  const pathname = usePathname();
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    sentinelRef.current?.focus({ preventScroll: true });
  }, [pathname]);

  return (
    <span
      ref={sentinelRef}
      tabIndex={-1}
      aria-hidden="true"
      className="sr-only"
    />
  );
}
